import type React from "react";
import { useEffect, useState } from "react";
import "./QuizComponent.scss";
import { Button, Loader } from "rsuite";
import { useActivityCompletion } from "../../../hooks/useActivitiesCompletion";
import { Fireworks } from "../../../shared/components/Fireworks/Fireworks";
import { CorrectAnswers } from "./components/CorrectAnswers";
import type { Answer, Question } from "./types";

type Props = {
	questions: Question[];
	activityId: string;
};

const shuffleArray = (array: Answer[]) => {
	for (let i = array.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[array[i], array[j]] = [array[j], array[i]];
	}
	return array;
};

const QuizComponent: React.FC<Props> = ({ questions, activityId }) => {
	const [currentQuestion, setCurrentQuestion] = useState(0);
	const [showScore, setShowScore] = useState(false);
	const [score, setScore] = useState(0);
	const [shuffledQuestions, setShuffledQuestions] = useState<Question[] | null>(
		null,
	);
	const [showCorrectAnswers, setShowCorrectAnswers] = useState(false);
	const [fireworksTrigger, setFireworksTrigger] = useState(false);
	const { triggerActivityCompletion } = useActivityCompletion();

	useEffect(() => {
		if (questions.length) {
			const shuffled = questions.map((question) => {
				return {
					...question,
					answers: shuffleArray([...question.answers]),
				};
			});
			setShuffledQuestions(shuffled);
		}
	}, [questions]);

	const handleAnswerOptionClick = async (isCorrect: boolean) => {
		if (isCorrect) {
			setScore(score + 1);
		}

		const nextQuestion = currentQuestion + 1;
		if (nextQuestion < questions.length) {
			setCurrentQuestion(nextQuestion);
		} else {
			setShowScore(true);
			await triggerActivityCompletion(activityId, score);
			setFireworksTrigger(true);
		}
	};

	if (!shuffledQuestions) {
		return <Loader center content="loading" vertical />;
	}

	const resetQuiz = () => {
		setCurrentQuestion(0);
		setShowScore(false);
		setScore(0);
		setShowCorrectAnswers(false);
	};

	return (
		<div className="quiz-app">
			{fireworksTrigger && (
				<Fireworks
					trigger={fireworksTrigger}
					onComplete={() => setFireworksTrigger(false)}
				/>
			)}
			{showCorrectAnswers && <CorrectAnswers questions={shuffledQuestions} />}
			{showScore ? (
				<div className="score-section">
					<p>
						You scored {score} out of {shuffledQuestions.length}
					</p>
					<Button
						appearance="primary"
						type="button"
						onClick={() => setShowCorrectAnswers(!showCorrectAnswers)}
					>
						{showCorrectAnswers
							? "Hide correct answers"
							: " Show correct answers"}
					</Button>
					<Button appearance="primary" type="button" onClick={resetQuiz}>
						Try Again?
					</Button>
				</div>
			) : (
				<>
					<div className="question-section">
						<div className="question-count">
							<span>Question {currentQuestion + 1}</span>/
							{shuffledQuestions.length}
						</div>
						<div className="question-text">
							{shuffledQuestions[currentQuestion].question}
						</div>
					</div>
					<div className="answer-section">
						{shuffledQuestions[currentQuestion]?.answers?.map(
							(answerOption: Answer, index: number) => (
								<button
									type="button"
									key={answerOption.answer}
									className="quiz-btn"
									onClick={() => handleAnswerOptionClick(answerOption.correct)}
								>
									{answerOption.answer}
								</button>
							),
						)}
					</div>
				</>
			)}
		</div>
	);
};

export default QuizComponent;
